import { Getter, Action, Mutation } from 'vuex';
import { RootState, AICodingState } from '../types';
import * as types from '../mutation-types';
import { getTracker } from '@/tracking/trackingPlugin';
import { AiCodingEvent } from '@/tracking/trackingTypes';
import { AiCodingCode, AiIntentionalCodingQuestion } from '@/graphql/operations.gen';
import { pick } from 'ramda';
import { AiCodingQuotationWithCodes } from '@components/AICodingWizard/types';
import { createOrUpdateQuotationsOperation } from '@/store/operations/createOrUpdateQuotationsOperation';

// initial state
const initialState: AICodingState = {
  dontShowAiCodingInfo: false,
  current: {
    researchGoal: '',
    questions: [],
  },
};

const getLocalStorageKey = () => 'atlasticloud-aicodingconfig';

const getConfigFromStorage = (): AICodingState => {
  const item = localStorage.getItem(getLocalStorageKey());

  if (item) {
    return JSON.parse(item);
  }

  return { ...initialState };
};

const storeConfig = (data: AICodingState) => {
  localStorage.setItem(getLocalStorageKey(), JSON.stringify(data));
};

const updateStateAndStoreConfig = (state: AICodingState, newConfig: Partial<AICodingState>) => {
  Object.entries(newConfig).forEach(([key, value]) => {
    state[key] = value;
  });

  storeConfig(state);
};

export const pickCodeInput = pick<'id' | 'name' | 'comment' | 'isNewCode' | 'color'>([
  'id',
  'name',
  'comment',
  'isNewCode',
  'color',
]);

// getters
const getters: { [key: string]: Getter<AICodingState, RootState> } = {
  current(state) {
    return state.current;
  },
};

// actions
const actions: { [key: string]: Action<AICodingState, RootState> } = {
  createQuotationsAndCodes(
    _,
    data: {
      codes: Array<AiCodingCode>;
      parentCodes: Array<AiCodingCode>;
      quotations: Array<AiCodingQuotationWithCodes>;
      projectId: string;
    }
  ) {
    return createOrUpdateQuotationsOperation(data);
  },
  show({ dispatch, rootState }, data: { [key: string]: any }) {
    getTracker().track(AiCodingEvent.AiCodingOpened);

    dispatch(
      'modal/show',
      {
        type: 'AICodingWizard',
        payload: {
          user: rootState.user,
          ...data,
        },
      },
      { root: true }
    );
  },

  hide({ dispatch }) {
    dispatch(
      'modal/hide',
      {
        type: 'AICodingWizard',
      },
      { root: true }
    );
  },

  setup({ commit }) {
    commit(types.AI_CODING_INIT_CONFIG);
  },

  setResearchGoal({ commit }, value: string) {
    commit(types.AI_CODING_SET_RESEARCH_GOAL, value);
  },

  setQuestions({ commit }, value: string) {
    commit(types.AI_CODING_SET_QUESTIONS, value);
  },

  resetCurrent({ commit }) {
    commit(types.AI_CODING_RESET_CURRENT);
  },
};

const mutations: { [key: string]: Mutation<AICodingState> } = {
  [types.AI_CODING_INIT_CONFIG](state) {
    const config = getConfigFromStorage();

    if (config) {
      Object.entries(config).forEach(([key, value]) => {
        state[key] = value;
      });
    }
  },

  [types.AI_CODING_SET_DONT_SHOW_AI_CODING_INFO](state, value: boolean) {
    updateStateAndStoreConfig(state, { dontShowAiCodingInfo: value });
  },

  [types.AI_CODING_SET_RESEARCH_GOAL](state, value: string) {
    updateStateAndStoreConfig(state, { current: { ...state.current, researchGoal: value } });
  },

  [types.AI_CODING_SET_QUESTIONS](state, value: Array<AiIntentionalCodingQuestion>) {
    updateStateAndStoreConfig(state, { current: { ...state.current, questions: value } });
  },

  [types.AI_CODING_RESET_CURRENT](state) {
    updateStateAndStoreConfig(state, {
      ...initialState,
      dontShowAiCodingInfo: state.dontShowAiCodingInfo,
    });
  },
};

export default {
  namespaced: true,
  state: { ...initialState },
  getters,
  actions,
  mutations,
};
